import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import {
  cttqService,
  formatCoveragePercent,
  pickDoneParticles,
  type ConvergingTtqRecord,
  type CttqPortfolioHealth,
  type CttqPreviewResult,
} from '../../services/cttqService';
import { buildTtqWorkbenchSourceGroupItems } from '../../features/ttqib/buildTtqWorkbenchSourceGroupItems';
import { TtqWorkbenchSourceParticleGroup } from '../../features/ttqib/TtqWorkbenchSourceParticle';
import { ttqService, type TtqSession } from '../../services/ttqService';
import { CttqSessionHealthBanner } from './CttqHealthBanner';
import { SectionCard } from './SectionCard';
import { TtqCttqRedundancyModal } from './TtqCttqRedundancyModal';

type TtqCttqWorkbenchContentProps = {
  session: TtqSession;
  onSessionUpdated: (session: TtqSession) => void;
  onOpenParticle: (particleId: string) => void;
  /** 从 BottomMenu 带入的已选颗粒 */
  initialMemberIds?: string[];
  initialCttqParticleId?: string | null;
};

function chipClass(active: boolean) {
  return active
    ? 'border-primary bg-primary/10 text-primary'
    : 'border-border bg-surface text-ink hover:border-primary/40';
}

export function TtqCttqWorkbenchContent({
  session,
  onSessionUpdated,
  onOpenParticle,
  initialMemberIds,
  initialCttqParticleId,
}: TtqCttqWorkbenchContentProps) {
  const [records, setRecords] = useState<ConvergingTtqRecord[]>(session.cttqRecords ?? []);
  const [portfolioHealth, setPortfolioHealth] = useState<CttqPortfolioHealth | null>(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [preview, setPreview] = useState<CttqPreviewResult | null>(null);
  const [redundancyOpen, setRedundancyOpen] = useState(false);
  const [cttqParticleId, setCttqParticleId] = useState<string | null>(null);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);

  const doneParticles = useMemo(() => pickDoneParticles(session.particles), [session.particles]);
  const sourceGroupItems = useMemo(() => {
    const base = initialMemberIds?.length
      ? doneParticles.filter((e) => initialMemberIds.includes(e.particleId))
      : doneParticles;
    return buildTtqWorkbenchSourceGroupItems(base);
  }, [doneParticles, initialMemberIds]);

  const loadRecords = useCallback(async () => {
    try {
      setRecords(await cttqService.listRecords(session.id));
    } catch {
      setRecords(session.cttqRecords ?? []);
    }
  }, [session.cttqRecords, session.id]);

  const loadHealth = useCallback(async () => {
    setLoadingHealth(true);
    try {
      setPortfolioHealth(await cttqService.getPortfolioHealth(session.subjectCode));
    } catch {
      setPortfolioHealth(null);
    } finally {
      setLoadingHealth(false);
    }
  }, [session.subjectCode]);

  useEffect(() => {
    void loadRecords();
    void loadHealth();
  }, [loadHealth, loadRecords]);

  useEffect(() => {
    if (cttqParticleId || doneParticles.length === 0) return;
    const presetMembers = initialMemberIds?.filter((id) => doneParticles.some((e) => e.particleId === id));
    const members = presetMembers?.length ? presetMembers : doneParticles.map((e) => e.particleId);
    const cttq = initialCttqParticleId && doneParticles.some((e) => e.particleId === initialCttqParticleId)
      ? initialCttqParticleId
      : members[0] ?? doneParticles[0].particleId;
    setCttqParticleId(cttq);
    setSelectedMemberIds(members);
  }, [cttqParticleId, doneParticles, initialCttqParticleId, initialMemberIds]);

  const toggleMember = (particleId: string) => {
    setSelectedMemberIds((current) => (
      current.includes(particleId)
        ? current.filter((id) => id !== particleId)
        : [...current, particleId]
    ));
  };

  const confirmCreate = useCallback(async (acknowledged: boolean) => {
    if (!cttqParticleId || selectedMemberIds.length === 0) return;
    setCreating(true);
    try {
      await cttqService.createRecord(session.id, {
        cttqParticleId,
        convergedParticleIds: selectedMemberIds,
        userAcknowledgedRedundancy: acknowledged,
        isPrimary: records.length === 0,
      });
      setRedundancyOpen(false);
      await loadRecords();
      await loadHealth();
      onSessionUpdated(await ttqService.getSession(session.id));
      window.alert('CTTQ 已创建');
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '创建失败');
    } finally {
      setCreating(false);
    }
  }, [cttqParticleId, loadHealth, loadRecords, onSessionUpdated, records.length, selectedMemberIds, session.id]);

  const handlePreviewCreate = async () => {
    if (!cttqParticleId || selectedMemberIds.length === 0) {
      window.alert('请选择至少 1 个 member 颗粒');
      return;
    }
    setPreviewing(true);
    try {
      const result = await cttqService.previewCreate(session.id, {
        cttqParticleId,
        convergedParticleIds: selectedMemberIds,
      });
      setPreview(result);
      if (result.redundancyReport.redundancyScore >= 0.55) {
        setRedundancyOpen(true);
      } else {
        await confirmCreate(false);
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '预览失败');
    } finally {
      setPreviewing(false);
    }
  };

  const completeSession = async (createAnchorIdentity: boolean) => {
    setCompleting(true);
    try {
      await cttqService.completeTtqa(session.id, createAnchorIdentity);
      onSessionUpdated(await ttqService.getSession(session.id));
      window.alert('品题活动已标记为 completed');
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '完成失败');
    } finally {
      setCompleting(false);
    }
  };

  const handleComplete = () => {
    const activeCount = records.filter((r) => r.lifecycle === 'active').length;
    if (activeCount === 0) {
      if (window.confirm('缺少 active CTTQ。是否生成 anchor 兜底（仅自代表）？')) {
        void completeSession(true);
      }
      return;
    }
    if (window.confirm('确认完成品题活动？须已有 active CTTQ。')) {
      void completeSession(false);
    }
  };

  const particleLabel = (entry: (typeof doneParticles)[number]) =>
    entry.particleTitle?.trim() || entry.particleId.slice(0, 8);

  return (
    <div className="space-y-4">
      {sourceGroupItems.length > 0 ? (
        <TtqWorkbenchSourceParticleGroup
          items={sourceGroupItems}
          activeParticleId={cttqParticleId ?? undefined}
          onActiveParticleChange={setCttqParticleId}
        />
      ) : null}

      {loadingHealth ? (
          <div className="flex items-center gap-2 text-sm text-muted">
            <Loader2 className="animate-spin" size={16} />
            正在加载收敛健康度…
          </div>
        ) : (
          <CttqSessionHealthBanner health={portfolioHealth} />
        )}

        {records.length > 0 ? (
          <ul className="divide-y divide-border rounded-xl border border-border">
            {records.map((record) => (
              <li key={record.id}>
                <button
                  type="button"
                  className="flex w-full flex-col items-start px-4 py-3 text-left hover:bg-primary/5"
                  onClick={() => onOpenParticle(record.cttqParticleId)}
                >
                  <span className="font-medium text-ink">
                    ★ {formatCoveragePercent(record.coverageReport.overallScore)}
                    {' · '}
                    {record.convergedParticleIds.length} 道 member
                  </span>
                  <span className="text-xs text-muted">
                    {record.reviewPriorityTier === 'deprioritized' ? '低优先级' : '首要安排'}
                    {' · '}
                    {record.lifecycle}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted">尚未创建收敛品题</p>
        )}

        {doneParticles.length >= 1 ? (
          <div className="space-y-4 rounded-xl border border-dashed border-border p-4">
            <div>
              <p className="mb-2 text-xs font-semibold text-muted">代表题（CTTQ 颗粒）</p>
              <div className="flex flex-wrap gap-2">
                {doneParticles.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    className={`rounded-full border px-3 py-1.5 text-xs ${chipClass(cttqParticleId === entry.particleId)}`}
                    onClick={() => setCttqParticleId(entry.particleId)}
                  >
                    {particleLabel(entry)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold text-muted">被收敛 member</p>
              <div className="flex flex-wrap gap-2">
                {doneParticles.map((entry) => {
                  const selected = selectedMemberIds.includes(entry.particleId);
                  return (
                    <button
                      key={`m-${entry.id}`}
                      type="button"
                      className={`rounded-full border px-3 py-1.5 text-xs ${chipClass(selected)}`}
                      onClick={() => toggleMember(entry.particleId)}
                    >
                      {selected ? '✓ ' : ''}
                      {particleLabel(entry)}
                    </button>
                  );
                })}
              </div>
            </div>
            <button
              type="button"
              className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              disabled={previewing || creating || !cttqParticleId || selectedMemberIds.length === 0}
              onClick={() => { void handlePreviewCreate(); }}
            >
              {previewing ? '预览中…' : '预览并创建 CTTQ'}
            </button>
          </div>
        ) : null}

        {session.status !== 'completed' ? (
          <button
            type="button"
            className="w-full rounded-lg border border-border px-4 py-2.5 text-sm text-ink hover:bg-black/5 disabled:opacity-50"
            disabled={completing}
            onClick={handleComplete}
          >
            {completing ? '完成中…' : '完成品题活动'}
          </button>
        ) : (
          <p className="text-sm text-muted">品题活动已完成</p>
        )}

      <TtqCttqRedundancyModal
        open={redundancyOpen}
        preview={preview}
        creating={creating}
        onClose={() => setRedundancyOpen(false)}
        onConfirmCreate={(ack) => { void confirmCreate(ack); }}
      />
    </div>
  );
}

/** @deprecated 品题页已移除内嵌 CTTQ 卡，请使用 TtqCttqWorkbenchPage */
export function TtqCttqPanel(props: TtqCttqWorkbenchContentProps) {
  return (
    <SectionCard title="收敛品题（CTTQ）" subtitle="一题替多题，减轻复习负担" tone="accent">
      <TtqCttqWorkbenchContent {...props} />
    </SectionCard>
  );
}
